/**
 * Provisioning-setup access resolution for the setup shell.
 * Uses GET /provisioning-setup/access only — never the active /access endpoint.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchProvisioningSetupAccess,
  type ProvisioningSetupAccessEntry,
} from "../api/cognitoryProvisioningSetupAccessClient";
import { isValidCognitoryWorkspaceId } from "../presentation/cognitoryWorkspaceNavigation";

export type ProvisioningSetupAccessPhase =
  | "idle_signed_out"
  | "invalid_request"
  | "resolving"
  | "authorized"
  | "forbidden"
  | "unauthenticated"
  | "temporary_failure"
  | "unable_to_verify";

export type ProvisioningSetupAccessState = {
  phase: ProvisioningSetupAccessPhase;
  cognitory: ProvisioningSetupAccessEntry | null;
  requestId: string | null;
  retry: () => void;
};

export function selectProvisioningSetupCognitoryIfPermitted(input: {
  cognitoryId: string;
  authorizedCognitories: ProvisioningSetupAccessEntry[];
}): ProvisioningSetupAccessEntry | null {
  if (!isValidCognitoryWorkspaceId(input.cognitoryId)) {
    return null;
  }
  return (
    input.authorizedCognitories.find(
      entry => entry.cognitoryId === input.cognitoryId,
    ) ?? null
  );
}

export function useProvisioningSetupAccess(input: {
  signedIn: boolean;
  requestedCognitoryId: string | null;
}): ProvisioningSetupAccessState {
  const [phase, setPhase] = useState<ProvisioningSetupAccessPhase>(
    input.signedIn ? "resolving" : "idle_signed_out",
  );
  const [cognitory, setCognitory] =
    useState<ProvisioningSetupAccessEntry | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const retry = useCallback(() => {
    setReloadToken(value => value + 1);
  }, []);

  useEffect(() => {
    if (!input.signedIn) {
      setPhase("idle_signed_out");
      setCognitory(null);
      setRequestId(null);
      return;
    }

    if (!input.requestedCognitoryId) {
      setPhase("invalid_request");
      setCognitory(null);
      setRequestId(null);
      return;
    }

    if (!isValidCognitoryWorkspaceId(input.requestedCognitoryId)) {
      setPhase("invalid_request");
      setCognitory(null);
      setRequestId(null);
      return;
    }

    let cancelled = false;
    setPhase("resolving");
    setCognitory(null);
    setRequestId(null);

    void (async () => {
      const result = await fetchProvisioningSetupAccess();
      if (cancelled) return;

      setRequestId(result.requestId);
      if (result.kind === "unauthenticated") {
        setPhase("unauthenticated");
        return;
      }
      if (result.kind === "temporary_failure") {
        setPhase("temporary_failure");
        return;
      }
      if (result.kind === "unable_to_verify") {
        setPhase("unable_to_verify");
        return;
      }
      if (result.kind === "resolved") {
        const match = selectProvisioningSetupCognitoryIfPermitted({
          cognitoryId: input.requestedCognitoryId!,
          authorizedCognitories: result.cognitories,
        });
        if (!match) {
          setPhase("forbidden");
          setCognitory(null);
          return;
        }

        setCognitory(match);
        setPhase("authorized");
        return;
      }

      setPhase("unable_to_verify");
    })();

    return () => {
      cancelled = true;
    };
  }, [input.signedIn, input.requestedCognitoryId, reloadToken]);

  return {
    phase,
    cognitory,
    requestId,
    retry,
  };
}
